import React from "react";
import DOMUtils from "../../utils/DOMUtils";

import Rectangle from "../../utils/math/Rectangle";
import CollisionTester from "../../utils/math/CollisionTester";
import SelectionActions from "../../actions/SelectionActions";
import GroupStore from "@/stores/GroupStore";

class ElementSelector extends React.Component {
  static defaultProps = {
    active: false,
    zoom: 1,
  };

  constructor(props) {
    super(props);

    this.state = {
      isDragging: false,
      end: null,
      selector: null,
    };

    // not state: a touch's press and its first move can arrive in one event,
    // before React applies a state update
    this.selectStart = null;
    this.selectedBlock = null;
    this.skipElementId = null;
    // a pen and a finger are each their type's primary pointer: only the one
    // that pressed draws the marquee
    this.selectPointerId = null;
  }

  _onMouseDown = (e) => {
    if (!e.isPrimary) return;
    let target = e.target;
    const locked = target?.classList.contains("locked");

    if (target) {
      let id = target.getAttribute("id");
      let selectedBlock = this.element.querySelector("div[data-selected=true]");
      let isElement = id && id.indexOf("si_element_") >= 0;

      if ((selectedBlock && !isElement) || (isElement && locked)) {
        const { clientX, clientY } = e.nativeEvent;
        this.selectStart = {
          x: Math.round(clientX),
          y: Math.round(clientY),
        };

        this.selectedBlock = selectedBlock;
        this.selectPointerId = e.pointerId;

        if (isElement) {
          this.skipElementId = id;
        }

        SelectionActions.setDragSelecting(true);
        window.addEventListener("pointermove", this._onMouseMove);
        window.addEventListener("pointerup", this._onMouseUp);
        window.addEventListener("pointercancel", this._onMouseUp);
      }
    }
  };

  _onMouseMove = (e) => {
    if (e.pointerId !== this.selectPointerId) return;
    let nextState = { isDragging: this.state.isDragging };
    const start = this.selectStart;

    const { clientX, clientY } = e;

    const mousePosition = {
      x: Math.round(clientX),
      y: Math.round(clientY),
    };

    if (!nextState.isDragging) {
      if (
        Math.abs(start.x - mousePosition.x) >= 2 ||
        Math.abs(start.y - mousePosition.y) >= 2
      ) {
        nextState.isDragging = true;
      }
    }

    if (nextState.isDragging) {
      nextState.end = { ...mousePosition };
      nextState.selector = this._getSelectionRect(start, nextState.end);
    }

    this.setState(nextState);
  };

  _getSelectionRect(start, end) {
    const { zoom = 1 } = this.props;
    // container rect and mouse coords are visual px; divide by zoom to get
    // design px for the selector div rendered inside the scaled overlay
    let container = this.element.getBoundingClientRect();

    let rect = {
      top: (Math.min(start.y, end.y) - container.top) / zoom,
      left: (Math.min(start.x, end.x) - container.left) / zoom,
      width: Math.abs(start.x - end.x) / zoom,
      height: Math.abs(start.y - end.y) / zoom,
    };

    return rect;
  }

  _onMouseUp = (e) => {
    if (e.pointerId !== this.selectPointerId) return;
    this._removeMouseListeners();

    let toSelect = [];

    if (this.state.isDragging) {
      const { selector } = this.state;
      const { onSelect } = this.props;

      let selTL = {
        x:
          selector.left -
          (this.element.clientWidth - this.selectedBlock.clientWidth) / 2,
        y: selector.top - this.selectedBlock.offsetTop,
      };

      let selBR = {
        x: selTL.x + selector.width,
        y: selTL.y + selector.height,
      };

      let selectionRect = new Rectangle(
        selTL,
        { x: selBR.x, y: selTL.y },
        { x: selTL.x, y: selBR.y },
        selBR,
      );

      let children = this.selectedBlock.childNodes;
      const focusedGroupID = GroupStore.getGroupId();

      children.forEach((child) => {
        if (child.getAttribute("id") !== this.skipElementId) {
          let rect = DOMUtils.getRectangle(child);

          if (CollisionTester.isColliding(selectionRect, rect)) {
            // filter ungrouped elements from selection if focused
            const canSelect = focusedGroupID
              ? child.getAttribute("data-parent-id") === focusedGroupID
              : true;

            canSelect && toSelect.push(child.getAttribute("data-id"));
          }
        }
      });

      if (toSelect.length > 0 && onSelect) {
        setTimeout(() => {
          onSelect({ ids: toSelect, addTo: e.shiftKey });
        }, 0);
      }
    }
    this.selectStart = null;
    this.selectedBlock = null;
    this.skipElementId = null;
    this.selectPointerId = null;
    this.setState({
      isDragging: false,
      selector: null,
      end: null,
      blockStart: null,
    });
    SelectionActions.setDragSelecting(false);
  };

  componentWillUnmount() {
    this.selectedBlock = null;
    this.skipElementId = null;
    this.selectPointerId = null;
    this._removeMouseListeners();
  }

  _removeMouseListeners = () => {
    window.removeEventListener("pointermove", this._onMouseMove);
    window.removeEventListener("pointerup", this._onMouseUp);
    window.removeEventListener("pointercancel", this._onMouseUp);
  };

  _setRef = (element) => {
    this.element = element;
  };

  render() {
    let onMouseDown = null;
    let selectorComp = null;

    let { isDragging, selector } = this.state;

    if (this.props.active) {
      onMouseDown = this._onMouseDown;
    }

    if (isDragging && selector) {
      /** @type {React.CSSProperties} */
      let style = {
        position: "absolute",
        top: selector.top,
        left: selector.left,
        width: selector.width,
        height: selector.height,
        backgroundColor: "rgba(0,0,0,.2)",
        border: "dashed 1px white",
        outline: "dashed 1px black",
      };
      selectorComp = (
        <div className="si-c-stage__drag-selector" style={style} />
      );
    }

    return (
      <div
        id={this.props.id}
        ref={this._setRef}
        style={this.props.style}
        onPointerDownCapture={onMouseDown}
        onContextMenu={this.props.onContextMenu}
      >
        {selectorComp}
        {this.props.children}
      </div>
    );
  }
}

export default ElementSelector;
